<template>
  <div class="layout-wrapper">
    <a href="#main-content" class="skip-link">{{ t('common.skipToContent') }}</a>

    <!-- Live demo: thin context banner above the real app chrome. -->
    <DemoBanner v-if="demoStore.active" />

    <div class="layout-main">
      <AppTopbar @logout="handleLogout" />

      <main id="main-content" tabindex="-1" class="layout-content">
        <!-- Single dismissible hint — the only guidance layered on the demo. -->
        <div v-if="demoStore.active && showDemoHint" class="demo-hint" role="note">
          <i class="pi pi-info-circle" aria-hidden="true" />
          <span>{{ t('demo.hint.body') }}</span>
          <button
            type="button"
            class="demo-hint-close"
            :aria-label="t('common.close')"
            @click="showDemoHint = false"
          >
            <i class="pi pi-times" aria-hidden="true" />
          </button>
        </div>
        <slot />
      </main>
    </div>

    <!-- Bottom dock navigation (scoped to safe modules in the demo) -->
    <AppDock :demo="demoStore.active" />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useDemoStore } from '@/stores/demo'
import AppTopbar from './AppTopbar.vue'
import AppDock from './AppDock.vue'
import DemoBanner from './DemoBanner.vue'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const demoStore = useDemoStore()

const showDemoHint = ref(true)

const handleLogout = () => {
  authStore.logout()
  router.push('/login')
}
</script>

<style scoped>
/* Skip link: off-screen until keyboard-focused, then pinned top-left. */
.skip-link {
  position: absolute;
  left: var(--app-spacing-sm);
  top: -100%;
  z-index: var(--app-z-tooltip);
  padding: var(--app-spacing-sm) var(--app-spacing-md);
  background: var(--app-primary-color);
  color: #fff;
  border-radius: var(--app-radius-md);
  font-weight: 600;
  text-decoration: none;
  transition: top var(--app-animation-duration-fast) ease;
}

.skip-link:focus-visible,
.skip-link:focus {
  top: var(--app-spacing-sm);
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.layout-content:focus {
  outline: none;
}

/* Live-demo hint: one dismissible callout, not a tour. */
.demo-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: var(--app-spacing-md);
  padding: 0.6rem 0.85rem;
  background: color-mix(in srgb, var(--app-primary-color) 10%, var(--app-surface-card));
  border: 1px solid color-mix(in srgb, var(--app-primary-color) 30%, transparent);
  border-radius: var(--app-radius-md);
  font-size: 0.875rem;
  color: var(--app-text-primary);
}
.demo-hint .pi-info-circle {
  color: var(--app-primary-color);
}
.demo-hint-close {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: var(--app-text-muted);
  cursor: pointer;
  padding: 0.2rem;
  line-height: 1;
}
.demo-hint-close:hover {
  color: var(--app-text-primary);
}

.layout-wrapper {
  display: flex;
  flex-direction: column;
  /* Fixed viewport height + internal scroll so the dock's reserved clearance
     stays in the visible area (page-level scroll would push it off-screen). */
  height: 100vh;
  overflow: hidden;
  background-color: var(--app-surface);
  transition: background-color var(--app-transition-base);
}

.layout-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  /* Reserve a real empty strip at the bottom for the fixed dock to float over.
     Because this shrinks the scroll area itself (not just pads its content),
     even PrimeVue flex-scroll tables end their paginator above the dock.
     Measured by AppDock via ResizeObserver; 6.5rem is the pre-hydration value. */
  padding-bottom: var(--app-dock-clearance, 6.5rem);
}

.layout-content {
  flex: 1;
  min-height: 0;
  padding: var(--app-spacing-lg);
  overflow-y: auto;
  background-color: var(--app-surface);
  transition: background-color var(--app-transition-base);
}

.container {
  max-width: var(--app-container-max-width);
  margin: 0 auto;
  width: 100%;
}

/* Responsive design (dock clearance handled on .layout-main) */
@media (max-width: 768px) {
  .layout-content {
    padding: var(--app-spacing-md);
  }
}

@media (max-width: 480px) {
  .layout-content {
    padding: var(--app-spacing-sm);
  }
}

/* Smooth transitions for theme changes */
@media (prefers-reduced-motion: reduce) {
  .layout-main,
  .layout-content,
  .layout-wrapper {
    transition: none;
  }
}
</style>
